import React, { useState, useEffect } from "react";
import API_URL from "../../api/API";
import { useAdminAuthContext } from "../admin/useAdminAuthContext";
import { RichTextEditor } from "../../components/RichTextEditor";

export const AdminAdultFullWaiver = ({ service }) => {
  const { admin } = useAdminAuthContext();
  const [blocks, setBlocks] = useState([]);
  const [editingBlockIndex, setEditingBlockIndex] = useState(null);
  const [formData, setFormData] = useState({ title: "", content: "", status: "editable" });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  // Track collapsed state per block
  const [collapsed, setCollapsed] = useState({});
  // Counter to reset RichTextEditor after adding/updating
  const [editorResetCounter, setEditorResetCounter] = useState(0);

  const [showPreview, setShowPreview] = useState(false);


  // Fetch waiver blocks on mount
  useEffect(() => {
    const fetchWaiver = async () => {
      if (!service?._id || !service?.businessType?._id) return;
      setLoading(true);
      try {
        const response = await fetch(API_URL.ADMIN_ADULT_FULL_WAIVER_GET, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${admin.token}`,
          "x-session-id": admin.sessionId,             
          "x-frontend-url": window.location.pathname,
          },
          body: JSON.stringify({
            serviceId: service._id,
            businessTypeId: service.businessType._id,
          }),
        });

        const data = await response.json();

        if (data.success && Array.isArray(data.data?.sections)) {
          const fetchedBlocks = data.data.sections.map((section) => ({
            title: section.title,
            content: section.content,
            status: section.status,
            orderId: section.order,
          }));
          setBlocks(fetchedBlocks);

          // Initialize collapsed state
          const collapsedState = {};
          fetchedBlocks.forEach((_, idx) => (collapsedState[idx] = false));
          setCollapsed(collapsedState);
        } else {
          setBlocks([]);
        }
      } catch (err) {
        console.error("Fetch error:", err);
        alert("Error loading waiver blocks.");
      } finally {
        setLoading(false);
      }
    };

    fetchWaiver();
  }, [service, admin.token]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const saveBlock = () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      alert("Both title and content are required.");
      return;
    }

    const updatedBlocks = [...blocks];
    const newBlock = {
      title: formData.title,
      content: formData.content,
      orderId: editingBlockIndex !== null
        ? updatedBlocks[editingBlockIndex].orderId
        : blocks.length + 1,
      status: formData.status,
    };

    if (editingBlockIndex !== null) {
      updatedBlocks[editingBlockIndex] = newBlock;
    } else {
      updatedBlocks.push(newBlock);
    }

    setBlocks(updatedBlocks);
    setFormData({ title: "", content: "", status: "editable" });
    setEditingBlockIndex(null);
    setCollapsed((prev) => ({ ...prev, [updatedBlocks.length - 1]: false }));
    setEditorResetCounter(prev => prev + 1); // ✅ Reset editor
  };

  const editBlock = (index) => {
    const b = blocks[index];
    setFormData({ title: b.title, content: b.content, status: b.status });
    setEditingBlockIndex(index);
  };

  const deleteBlock = (index) => {
    if (!window.confirm("Delete this block?")) return;
    const updated = blocks.filter((_, i) => i !== index);
    setBlocks(updated);

    const newCollapsed = { ...collapsed };
    delete newCollapsed[index];
    setCollapsed(newCollapsed);

    if (editingBlockIndex === index) {
      setEditingBlockIndex(null);
      setFormData({ title: "", content: "", status: "editable" });
      setEditorResetCounter(prev => prev + 1);
    }
  };

  const moveBlock = (index, direction) => {
    const newBlocks = [...blocks];
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newBlocks.length) return;

    [newBlocks[index], newBlocks[targetIndex]] = [newBlocks[targetIndex], newBlocks[index]];

    const reOrdered = newBlocks.map((b, i) => ({ ...b, orderId: i + 1 }));
    setBlocks(reOrdered);

    const newCollapsed = { ...collapsed };
    const temp = newCollapsed[index];
    newCollapsed[index] = newCollapsed[targetIndex];
    newCollapsed[targetIndex] = temp;
    setCollapsed(newCollapsed);
  };

  const toggleCollapse = (index) => {
    setCollapsed((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const saveToServer = async () => {
    if (blocks.length === 0) {
      alert("Please add at least one block.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        serviceId: service._id,
        businessTypeId: service.businessType._id,
        sections: blocks,
      };

      const res = await fetch(API_URL.ADMIN_ADULT_FULL_WAIVER_SAVE, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${admin.token}`,
          "x-session-id": admin.sessionId,             
          "x-frontend-url": window.location.pathname,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || "Save failed.");
      alert("Waiver saved successfully!");
    } catch (err) {
      console.error("Save error:", err);
      alert("Error saving waiver: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const decodeHTML = (html) => {
    const txt = document.createElement("textarea");
    txt.innerHTML = html;
    return txt.value;
  };

  // sanitize HTML: close lists and ensure paragraphs outside lists
// inside AdminAdultFullWaiver component
const sanitizeWaiverHTML = (html) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const body = doc.body;

  // Find all <ul> and <ol>
  body.querySelectorAll("ul, ol").forEach(list => {
    // Only move trailing <p> that follows the list
    const lastLi = list.lastElementChild;
    if (lastLi && lastLi.tagName === "LI") {
      const lastChild = lastLi.lastElementChild;
      if (lastChild && lastChild.tagName === "P") {
        // leave it, do nothing — it's part of the bullet
      }
    }
  });

  // Trim all text
  body.querySelectorAll("*").forEach(el => {
    if (el.childNodes.length === 1 && el.childNodes[0].nodeType === 3) {
      el.textContent = el.textContent.trim();
    }
  });

  return body.innerHTML;
};


  
  

  return (
    <div className="flex gap-6 h-[600px]">
      {/* Left Panel */}
      <div className="w-1/2 border rounded bg-gray-50 p-3 overflow-y-auto">
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-semibold">Existing Blocks</h3>
        {blocks.length > 0 && (
          <button
            onClick={() => setShowPreview(true)}
            className="text-xs text-blue-600 underline hover:text-blue-800"
          >
            Preview Full Waiver
          </button>
        )}
      </div>

        {loading ? (
          <p className="text-xs text-gray-500">Loading...</p>
        ) : blocks.length === 0 ? (
          <p className="text-xs text-gray-500">No blocks yet</p>
        ) : (
          blocks
            .sort((a, b) => a.orderId - b.orderId)
            .map((block, idx) => (
              <div key={idx} className="border rounded mb-3 bg-white text-sm">
                <div className="flex justify-between items-center p-3 cursor-pointer" onClick={() => toggleCollapse(idx)}>
                  <div className="flex items-center gap-2">
                    <strong>{block.orderId}. {decodeHTML(block.title)}</strong>
                    <span className={`px-2 py-0.5 rounded text-[10px] ${block.status === "locked" ? "bg-red-200 text-red-700" : "bg-green-200 text-green-700"}`}>
                      {block.status}
                    </span>
                  </div>
                  <span className="text-xs">{collapsed[idx] ? "▼" : "▲"}</span>
                </div>
                {!collapsed[idx] && (
                  <div className="p-3 border-t text-gray-700">
                 <div
              className="prose max-w-none [&_ul]:list-disc [&_ul]:ml-6 [&_ol]:list-decimal [&_ol]:ml-6 [&_li]:mb-2 [&_p]:mb-4 [&_p]:ml-0"
              dangerouslySetInnerHTML={{ __html: sanitizeWaiverHTML(block.content) }}
            
                />

                    <div className="flex gap-2 mt-2">
                      <button onClick={() => moveBlock(idx, "up")} disabled={idx === 0} className="px-2 py-1 bg-gray-200 rounded text-xs">↑</button>
                      <button onClick={() => moveBlock(idx, "down")} disabled={idx === blocks.length - 1} className="px-2 py-1 bg-gray-200 rounded text-xs">↓</button>
                      <button onClick={() => editBlock(idx)} className="px-2 py-1 bg-blue-500 text-white rounded text-xs">Edit</button>
                      <button onClick={() => deleteBlock(idx)} className="px-2 py-1 bg-red-500 text-white rounded text-xs">Delete</button>
                    </div>
                  </div>
                )}
              </div>
            ))
        )}
      </div>

      {/* Right Panel */}
      <div className="flex-1 border rounded bg-white p-4 overflow-y-auto">
        <h3 className="font-semibold mb-3">{editingBlockIndex !== null ? "Edit Block" : "Add New Block"}</h3>

        <label className="block text-xs font-semibold mb-1">Title</label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => handleChange("title", e.target.value)}
          className="border rounded p-2 w-full mb-3 text-sm"
          placeholder="Enter block title"
        />

        <label className="block text-xs font-semibold mb-1">Content</label>
        <RichTextEditor
          key={editorResetCounter} // ✅ reset editor
          content={formData.content}
          onChange={(value) => handleChange("content", value)}
        />

        <label className="block text-xs font-semibold mb-1 mt-3">Status</label>
        <select
          value={formData.status}
          onChange={(e) => handleChange("status", e.target.value)}
          className="border rounded p-2 w-full text-sm"
        >
          <option value="editable">Editable</option>
          <option value="locked">Locked</option>
        </select>

        <div className="mt-4 flex gap-2">
            <button
              onClick={saveBlock}
              className="px-4 py-2 bg-green-500 text-white rounded text-sm"
            >
              {editingBlockIndex !== null ? "Update Block" : "Add Block"}
            </button>

            {editingBlockIndex !== null && (
              <button
                onClick={() => {
                  setEditingBlockIndex(null);
                  setFormData({ title: "", content: "", status: "editable" });
                  setEditorResetCounter(prev => prev + 1); // reset editor
                }}
                className="px-4 py-2 bg-gray-400 text-white rounded text-sm"
              >
                Cancel Edit
              </button>
            )}

            <button
              onClick={saveToServer}
              disabled={saving}
              className="px-4 py-2 bg-purple-500 text-white rounded text-sm"
            >
              {saving ? "Saving..." : "Save Full Waiver"}
            </button>
        </div>

      </div>


      {showPreview && (
  <div className="fixed inset-0 bg-black bg-opacity-40 flex flex-col items-center justify-center z-50">
    <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto p-10 shadow-2xl relative font-serif">
      {/* Close button */}
      <button
        onClick={() => setShowPreview(false)}
        className="absolute top-4 right-4 text-gray-600 hover:text-black text-lg font-bold"
      >
        ✕
      </button>

      {/* Document Header */}
      <div className="text-center mb-10">
        <h1 className="text-3xl font-bold mb-1">Adult Full Waiver</h1>
        <p className="text-gray-500 text-sm">
          Document Preview
        </p>
      </div>

      {/* Waiver Body */}
      <div className="space-y-10 text-gray-900 leading-relaxed">
        {blocks
          .sort((a, b) => a.orderId - b.orderId)
          .map((block, idx) => (
            <div key={idx}>
              <h2 className="text-xl font-semibold mb-3 border-b pb-1">
                {decodeHTML(block.title)}
              </h2>
              <div
    className="prose max-w-none [&_ul]:list-disc [&_ul]:ml-6 [&_ol]:list-decimal [&_ol]:ml-6 [&_li]:mb-2 [&_p]:mb-4 [&_p]:ml-0"
    dangerouslySetInnerHTML={{ __html: sanitizeWaiverHTML(block.content) }}
/>

            </div>
          ))}
      </div>
    </div>
  </div>
)}



    </div>





  );
};
